import React, { Suspense, lazy } from "react";
import ReactDOM from "react-dom/client";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./index.css";
import { AuthProvider } from "./state/AuthContext.jsx";
import { I18nProvider } from "./i18n/I18nContext.jsx";

// When a deploy changes chunk hashes, old HTML referencing old chunks 404s.
// Reload once per session to pick up fresh HTML; if it still fails, surface the error.
function lazyWithReload(importFn) {
  return lazy(() =>
    importFn().catch((err) => {
      if (!sessionStorage.getItem("chunk_reload")) {
        sessionStorage.setItem("chunk_reload", "1");
        window.location.reload();
        return new Promise(() => {});
      }
      throw err;
    })
  );
}

// Layouts
import BackOfficeLayout from "./components/layout/BackOfficeLayout.jsx";
const AnalyticsLayout = lazyWithReload(() => import("./components/layout/AnalyticsLayout.jsx"));
const ReportsLayout = lazyWithReload(() => import("./components/layout/ReportsLayout.jsx"));

// Auth pages (public)
const LoginScreen = lazyWithReload(() => import("./pages/loginscreen"));
const Launch = lazyWithReload(() => import("./pages/auth/Launch"));

// Back-office pages
const Dashboard = lazyWithReload(() => import("./pages/Dashboard"));
const MenuManagement = lazyWithReload(() => import("./pages/MenuManagement"));
const Inventory = lazyWithReload(() => import("./pages/Inventory"));
const Staff = lazyWithReload(() => import("./pages/Staff"));
const Settings = lazyWithReload(() => import("./pages/Settings"));
const DiscountManagement = lazyWithReload(() => import("./pages/DiscountManagement"));
const Reservations = lazyWithReload(() => import("./pages/Reservations"));
const Expenses = lazyWithReload(() => import("./pages/Expenses"));
const Suppliers = lazyWithReload(() => import("./pages/Suppliers"));
const Deliveries = lazyWithReload(() => import("./pages/Deliveries"));
const DeliveryForm = lazyWithReload(() => import("./pages/DeliveryForm"));
const DeliveryDetail = lazyWithReload(() => import("./pages/DeliveryDetail"));

// Analytics sub-pages
const RevenueTrends = lazyWithReload(() => import("./pages/analytics/RevenueTrends"));
const TopSellingItems = lazyWithReload(() => import("./pages/analytics/TopSellingItems"));
const InventoryAlerts = lazyWithReload(() => import("./pages/analytics/InventoryAlerts"));
const StaffPerformance = lazyWithReload(() => import("./pages/analytics/StaffPerformance"));
const OperationsDeepDive = lazyWithReload(() => import("./pages/analytics/OperationsDeepDive"));
const FinancialDeepDive = lazyWithReload(() => import("./pages/analytics/FinancialDeepDive"));

// Reports sub-pages
const AuditLogs = lazyWithReload(() => import("./pages/AuditLogs"));
const NapExports = lazyWithReload(() => import("./pages/NapExports"));
const MyReport = lazyWithReload(() => import("./pages/MyReport"));

// Account section
const AccountOverview = lazyWithReload(() => import("./pages/account/Overview"));
const AccountSubscription = lazyWithReload(() => import("./pages/account/Subscription"));
const AccountAddons = lazyWithReload(() => import("./pages/account/Addons"));
const AccountVenues = lazyWithReload(() => import("./pages/account/Venues"));

const S = ({ children }) => <Suspense fallback={<LoadingFallback />}>{children}</Suspense>;

const LoadingFallback = () => (
  <div className="flex items-center justify-center h-screen bg-[#0A0A0A]">
    <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

const router = createBrowserRouter([
  // ── Public auth routes ────────────────────────────────────────────────────
  {
    path: "/login",
    element: <S><LoginScreen /></S>,
  },
  {
    // SSO handoff from quantbar.net — ?t=<launch_token>
    path: "/auth/launch",
    element: <S><Launch /></S>,
  },

  // ── Authenticated back-office routes ──────────────────────────────────────
  {
    element: <S><BackOfficeLayout /></S>,
    children: [
      { index: true, element: <Navigate to="/dashboard" replace /> },

      { path: "dashboard", element: <S><Dashboard /></S> },

      // Analytics (with date/category filter layout)
      {
        path: "analytics",
        element: <S><AnalyticsLayout /></S>,
        children: [
          { index: true, element: <Navigate to="/analytics/revenue" replace /> },
          { path: "revenue",    element: <S><RevenueTrends /></S> },
          { path: "products",   element: <S><TopSellingItems /></S> },
          { path: "inventory",  element: <S><InventoryAlerts /></S> },
          { path: "staff",      element: <S><StaffPerformance /></S> },
          { path: "operations", element: <S><OperationsDeepDive /></S> },
          { path: "finance",    element: <S><FinancialDeepDive /></S> },
        ],
      },

      // Management
      { path: "menu",      element: <S><MenuManagement /></S> },
      { path: "inventory", element: <S><Inventory /></S> },
      { path: "staff",     element: <S><Staff /></S> },
      { path: "discounts", element: <S><DiscountManagement /></S> },
      { path: "reservations", element: <S><Reservations /></S> },
      { path: "expenses", element: <S><Expenses /></S> },
      { path: "suppliers",      element: <S><Suppliers /></S> },
      { path: "deliveries",     element: <S><Deliveries /></S> },
      { path: "deliveries/new", element: <S><DeliveryForm /></S> },
      { path: "deliveries/:id", element: <S><DeliveryDetail /></S> },
      { path: "settings",       element: <S><Settings /></S> },

      // Reports
      {
        path: "reports",
        element: <S><ReportsLayout /></S>,
        children: [
          { index: true, element: <Navigate to="/reports/nap" replace /> },
          { path: "nap",       element: <S><NapExports /></S> },
          { path: "audit",     element: <S><AuditLogs /></S> },
          { path: "my-report", element: <S><MyReport /></S> },
        ],
      },

      // Account (subscription, add-ons, venues — owner only)
      { path: "account",              element: <S><AccountOverview /></S> },
      { path: "account/subscription", element: <S><AccountSubscription /></S> },
      { path: "account/addons",       element: <S><AccountAddons /></S> },
      { path: "account/venues",       element: <S><AccountVenues /></S> },
    ],
  },
]);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <I18nProvider>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </AuthProvider>
    </I18nProvider>
  </React.StrictMode>
);
